import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild, HostListener } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import * as moment from 'moment';
import { APIService } from 'src/app/services/api.service';
import { NotifierService } from 'src/app/services/notifier.service';
import { checkForVenuePermission, getENV, convertTimestamp } from 'src/app/_helpers/helpers';
import { steps as defaultSteps, atc_steps } from '../pre-race/steps';
import { CommonModule } from '@angular/common';

interface NetworkDevice {
  instance: string;
  job: string;
  name: string;
  service: string;
  status: string;
  timestamp: string;
  status_message: string;
}

interface NetworkStatus {
  network_status: NetworkDevice[];
}

interface EventState {
  status: string;
  complete: boolean;
  assigned_pc?: string;
  assigned_operator?: string;
}

interface TrackAssignments {
  total_races: number;
  tracks_assigned: number;
  complete: boolean;
}

interface RailPosition {
  is_configured: boolean;
  rail_positions_set: number;
  complete: boolean;
}

interface KitAssignment {
  kit_id: string;
  is_tag_set: boolean;
  barrier_tag?: string;
  complete: boolean;
}

interface Startlist {
  pdf_generated: boolean;
  has_errors: boolean;
  is_running: boolean;
  pdf_file_path?: string;
  updated_at?: string;
  email_sent: boolean;
  complete: boolean;
}

interface Scratching {
  total_races: number;
  completed: number;
  pending: number;
  failed: number;
  complete: boolean;
}

interface RacePreparation {
  status: {
    event_state: EventState;
    track_assignments: TrackAssignments;
    rail_position: RailPosition;
    kit_assignment: KitAssignment;
    startlist: Startlist;
    scratchings: Scratching;
  };
  completion_percentage: number;
  all_completed: boolean;
}

interface PreparationStep {
  status: string;
  comments?: string;
  last_updated?: string;
}

interface Preparation {
  steps: { [key: string]: PreparationStep };
  total_steps: number;
  completed_steps: number;
  completion_percentage: number;
  all_completed: boolean;
}

interface Event {
  venue_name: string;
  external_event_id: string;
  external_venue_id: string;
  event_details?: {
    venue_name: string;
    event_id: string;
    event_unique_id: string;
    is_atc: boolean;
  };
  network_status?: NetworkDevice[];
  race_preparation?: RacePreparation;
  login_preparation?: Preparation;
  logout_preparation?: Preparation;
  expanded?: boolean;
  loading?: boolean;
  error?: string | null;
  showDetail?: boolean;
}

interface DateData {
  date: string;
  dateStr: string;
  loading: boolean;
  error?: string | null;
  events: Event[];
}

interface Step {
  key: string;
  status: string;
  comments?: string;
  last_updated?: string;
}

declare let $: any;

@Component({
  selector: 'app-modern-prepare-monitoring',
  templateUrl: './modern-prepare-monitoring.component.html',
  styleUrls: ['./modern-prepare-monitoring.component.css']
})
export class ModernPrepareMonitoringComponent implements OnInit, AfterViewInit, OnDestroy {
  currentDate: DateData = {
    date: moment().format('YYYY-MM-DD'),
    dateStr: moment().format('DD MMMM YYYY'),
    loading: true,
    events: []
  };

  config: any;
  error: string | null = null;

  constructor(
    private apiService: APIService,
    private notifier: NotifierService,
    private router: ActivatedRoute,
    private routerNav: Router
  ) {
    this.config = this.router.snapshot.data['config'];

    const queryDate = this.router.snapshot.queryParams['date'];
    if (queryDate && moment(queryDate, 'DD MMMM YYYY', true).isValid()) {
      this.currentDate.date = moment(queryDate, 'DD MMMM YYYY').format('YYYY-MM-DD');
      this.currentDate.dateStr = moment(queryDate).format('DD MMMM YYYY');
    }
  }

  ngOnInit(): void {
    this.loadEventsBasedOnDate(this.currentDate.date, this.currentDate);
  }

  ngAfterViewInit(): void {
    const today = new Date();
    const maxDate = new Date();
    maxDate.setDate(today.getDate() + 2);

    $('.datepicker').datepicker({
      autoclose: true,
      format: 'dd MM yyyy',
      orientation: 'bottom auto',
      endDate: maxDate,
      startDate: today, // Start from today
      defaultViewDate: today // Default selected date is today
    }).datepicker('setDate', today) // Set today's date as default selected
      .on('changeDate', (selected: any) => {
        const selectedDate = moment(selected.date);
        this.currentDate = {
          date: selectedDate.format('YYYY-MM-DD'),
          dateStr: selectedDate.format('DD MMMM YYYY'),
          loading: true,
          events: []
        };
        this.loadEventsBasedOnDate(this.currentDate.date, this.currentDate);
      });
  }

  ngOnDestroy(): void {}

  async loadEventsBasedOnDate(date: string, data: DateData): Promise<void> {
    data.loading = true;
    this.error = null;

    try {
      const apiURL = `${this.config['prod'].raceAPI}/event-list?formatted_date=${date}`;
      const result = await this.apiService.getDataPromis(apiURL, {}, {});

      if (result.eventNames && Array.isArray(result.eventNames)) {
        const filteredEvents = result.eventNames.filter((event: Event) =>
          checkForVenuePermission(event.external_venue_id)
        );

        const eventPromises = filteredEvents.map(async (event: Event) => {
          event.loading = true;
          try {
            const monitoringUrl = `${this.config['prod'].raceAPI}/prepare-monitoring?event_id=${event.external_event_id}`;
            const monitoringResult = await this.apiService.getDataPromis(monitoringUrl, {}, {});

            if (monitoringResult.status === 'success' && monitoringResult.events[event.external_event_id]) {
              const eventDetails = monitoringResult.events[event.external_event_id];
              return {
                ...event,
                ...eventDetails,
                loading: false,
                error: null
              };
            } else {
              this.notifier.alert(
                'Error',
                '',
                `Failed to load monitoring data for ${event.venue_name}`,
                'error'
              );
              return {
                ...event,
                loading: false,
                error: 'Failed to load monitoring data'
              };
            }
          } catch (error) {
            this.notifier.alert(
              'Error',
              '',
              `Error loading monitoring data for ${event.venue_name}`,
              'error'
            );
            return {
              ...event,
              loading: false,
              error: 'Failed to load monitoring data'
            };
          }
        });

        try {
          data.events = await Promise.all(eventPromises);
        } catch (error) {
          this.notifier.alert(
            'Error',
            '',
            'Failed to load event details',
            'error'
          );
          data.error = 'Failed to load event details';
        }
      } else {
        data.events = [];
      }
    } catch (error) {
      this.notifier.alert(
        'Error',
        '',
        'Failed to load events for the selected date',
        'error'
      );
      data.error = 'Failed to load events';
    }

    data.loading = false;
  }

  getProgressColor(percentage: number): string {
    if (percentage >= 80) return '#059669';
    if (percentage >= 40) return '#d97706';
    return '#dc2626';
  }

  getStatusColor(status: string): string {
    switch (status.toUpperCase()) {
      case 'COMPLETED':
        return '#28a745'; // Green
      case 'IN_PROGRESS':
        return '#ffc107'; // Yellow
      case 'FAILED':
        return '#fd7e14'; // Orange
      case 'ABANDONED':
        return '#dc3545'; // Red
      case 'PLANNED':
      default:
        return '#6c757d'; // Grey
    }
  }

  getSteps(type: 'login' | 'logout', isAtc: boolean): any[] {
    const stepsObj = isAtc ? atc_steps : defaultSteps;
    return stepsObj[type] || [];
  }

  getRacePreparationTitle(preparation: RacePreparation | undefined): string {
    if (!preparation) return 'No preparation data available';

    const status = preparation.status;
    const details = [];

    if (status.track_assignments) {
      details.push(`Tracks: ${status.track_assignments.tracks_assigned}/${status.track_assignments.total_races}`);
    }

    if (status.rail_position) {
      details.push(`Rail Positions: ${status.rail_position.rail_positions_set}`);
    }

    if (status.kit_assignment) {
      details.push(`Kit ID: ${status.kit_assignment.kit_id}`);
      if (status.kit_assignment.barrier_tag) {
        details.push(`Barrier Tag: ${status.kit_assignment.barrier_tag}`);
      }
    }

    if (status.startlist) {
      details.push(`Startlist: ${status.startlist.pdf_generated ? 'Generated' : 'Pending'}`);
      if (status.startlist.pdf_file_path) {
        details.push(`PDF: ${status.startlist.pdf_file_path}`);
      }
    }

    return details.join('\n');
  }

  getPreparationTitle(type: string, preparation: Preparation | undefined): string {
    if (!preparation) return `No ${type.toLowerCase()} preparation data available`;

    const details = [`${type} Preparation: ${preparation.completed_steps}/${preparation.total_steps} steps completed`];

    Object.entries(preparation.steps).forEach(([key, step]) => {
      details.push(`Step ${key}: ${step.status}`);
      if (step.comments) {
        details.push(`  Comments: ${step.comments}`);
      }
      if (step.last_updated) {
        details.push(`  Updated: ${moment(step.last_updated).format('DD MMM YYYY HH:mm')}`);
      }
    });

    return details.join('\n');
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    document.documentElement.style.setProperty('--cursor-y', `${event.clientY}px`);
  }

  shouldShowResponseTime(status: string): boolean {
    return status.toLowerCase() !== 'failed' && status.toLowerCase() !== 'error';
  }

  getStatusClass(status: string): string {
    if (!status) return 'planned';

    // Convert status to lowercase and replace spaces/special chars with underscore
    const normalizedStatus = status.toLowerCase()
      .replace(/[\s\/]+/g, '_')  // Replace spaces and forward slashes with underscore
      .replace(/[^a-z0-9_]/g, ''); // Remove any other special characters

    // List of valid statuses
    const validStatuses = [
      'planned', 'dns', 'dnt', 'dnf', 'dsq', 'official',
      'finished', 'published', 'abandoned', 'ftf', 'scr',
      'l_scr', 'fell', 'distance', 'ro', 'bd', 'lb',
      'lgo', 'lgt', 'in_progress', 'error'
    ];

    return validStatuses.includes(normalizedStatus) ? normalizedStatus : 'planned';
  }

  getNetworkStatusCount(networkStatus: NetworkDevice[] | undefined, status: string): number {
    if (!networkStatus) return 0;
    return networkStatus.filter(device => device.status === status).length;
  }

  getSortedNetworkDevices(devices: NetworkDevice[] | undefined): NetworkDevice[] {
    if (!devices) return [];
    return [...devices].sort((a, b) => {
      // Sort by status first (active/1 before inactive/0)
      if (a.status !== b.status) {
        return b.status.localeCompare(a.status);
      }
      // Then sort by service type
      if (a.service !== b.service) {
        return a.service.localeCompare(b.service);
      }
      // Finally sort by name
      return a.name.localeCompare(b.name);
    });
  }

  getDeviceStatus(status: string): 'success' | 'failed' {
    return status === '1' ? 'success' : 'failed';
  }

  getDeviceIcon(service: string): string {
    switch (service.toLowerCase()) {
      case 'trace':
        return 'fa-desktop';
      case 'router':
        return 'fa-network-wired';
      case 'ap':
        return 'fa-wifi';
      case 'basestation':
        return 'fa-broadcast-tower';
      case 'iot_gateway':
        return 'fa-hdd';
      case 'ns':
        return 'fa-server';
      case 'transceiver':
        return 'fa-signal';
      default:
        return 'fa-microchip';
    }
  }

  toggleEventDetail(event: Event): void {
    event.showDetail = !event.showDetail;
    // Close other event details
    this.currentDate.events.forEach(e => {
      if (e !== event) {
        e.showDetail = false;
      }
    });
  }

  getPrepareStatusSummary(event: Event): string {
    const prep = event.race_preparation;
    if (!prep) return 'Not started';

    const summary = [];

    if (prep.status.track_assignments) {
      const tracks = prep.status.track_assignments;
      summary.push(`Tracks: ${tracks.tracks_assigned}/${tracks.total_races}`);
    }

    if (prep.status.rail_position) {
      summary.push(`Rails: ${prep.status.rail_position.rail_positions_set}`);
    }

    if (prep.status.kit_assignment && prep.status.kit_assignment.kit_id) {
      summary.push(`Kit: ${prep.status.kit_assignment.kit_id}`);
    }

    return summary.length > 0 ? summary.join(' | ') : 'In preparation';
  }

  getStepArray(steps: { [key: string]: any } | undefined): Step[] {
    if (!steps) return [];
    return Object.entries(steps).map(([key, value]) => ({
      key,
      status: value.status,
      comments: value.comments,
      last_updated: value.last_updated
    }));
  }

  getStepIcon(status: string): string {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'fa-check-circle';
      case 'pending':
        return 'fa-clock';
      case 'in_progress':
        return 'fa-spinner fa-spin';
      case 'failed':
        return 'fa-times-circle';
      default:
        return 'fa-circle';
    }
  }
}
